import * as React from 'react';
import { Query, Mutation } from 'react-apollo';
import { useDebounce } from 'use-debounce';

import Error from './Error';
import { theme } from '../theme';
import H4 from '../typography/H4';
import Loader from './loaders/Loader';
import Span from '../typography/Span';
import RadioButtons from './RadioButtons';
import NoDataHandler from './NoDataHandler';
import { Button, StyledInput, Box } from '.';
import { User, Property } from '../utils/types';
import { formatGqlError } from '../utils/helpers';
import { SteynCityContext } from './ContextProvider';
import { StyledSearchTable } from './formElements/StyledSearch';
import { SearchStakeholders, SearchProperties, CreateApplication, GetAllApplications } from '../graphql';
import {
  residentSecurityAccessFields,
  securityAccessFields,
  contractorSecurityAccessFields,
  petApplicationFields,
  bankDebitOrderInstructionFields,
  vehicleSecurityAccessFields,
  gymMembershipFields,
  tennisFormFields,
  subcontractorSecurityAccessFields,
  shuttleServiceFields,
  specialAccessFields,
  crimCheckFields,
} from '../consts/applicationFormInitializations';
import { RouteComponentProps, withRouter } from 'react-router';
import { ComponentDecoratorProps, RowInfo } from 'react-table';

type Props = RouteComponentProps;

const APPLICATION_TYPES = [
  { value: 'BANK_DEBIT_ORDER_INSTRUCTION', label: 'Bank Debit Order Instruction' },
  { value: 'CONTRACTOR_SECURITY_ACCESS', label: 'Contractor Security Access' },
  { value: 'SUBCONTRACTOR_SECURITY_ACCESS', label: 'Sub Contractor Security Access' },
  { value: 'CRIMCHECK', label: 'Crimcheck' },
  { value: 'GYM_MEMBERSHIP', label: 'Gym Membership' },
  { value: 'PET', label: 'Pet Registration' },
  { value: 'SHUTTLE_SERVICE', label: 'Shuttle Service' },
  { value: 'SPECIAL_ACCESS', label: 'Special Access' },
  { value: 'RESIDENTS_SECURITY_ACCESS', label: 'Resident Security Access' },
  { value: 'SECURITY_ACCESS', label: 'Security Access' },
  { value: 'TENNIS_MEMBERSHIP', label: 'Tennis Membership' },
  { value: 'VEHICLE_SECURITY_ACCESS', label: 'Vehicle Security Access' },
];

const ApplicationsWizard: React.FunctionComponent<Props> = (props: Props) => {
  const [step, setStep] = React.useState(0);
  const [stakeholder, seStakeholder] = React.useState<User>(null);
  const [property, setProperty] = React.useState<Property>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);
  const [applicationType, setApplicationType] = React.useState<null | string>(null);
  const context = React.useContext(SteynCityContext);

  function renderSteps(currentStep: number) {
    switch (currentStep) {
      case 0:
        return (
          <Box justifyContent="flex-start" alignItems="flex-start" width="100%">
            <H4 fontWeight="lighter">Step 1: Select Stakeholder</H4>
            <StyledInput placeholder="Search..." onChange={(e: any) => setSearchTerm(e.target.value)} mb={4} mt={2} />
            <Query query={SearchStakeholders} variables={{ text: debouncedSearchTerm }}>
              {({ loading, error, data }) => {
                if (loading) {
                  return (
                    <Box width="100%" justifyContent="center" alignItems="center" mb={4}>
                      <Loader color={theme.colors.putty} />
                    </Box>
                  );
                }
                if (error) {
                  return <Error />;
                }
                return (
                  <StyledSearchTable
                    data={data.stakeholders}
                    columns={[
                      { Header: 'First Name', accessor: 'firstName' },
                      { Header: 'Surname', accessor: 'lastName' },
                    ]}
                    className="-striped -highlight"
                    defaultPageSize={5}
                    showPageSizeOptions={false}
                    getTrProps={(trprops: ComponentDecoratorProps, rowInfo: RowInfo) => {
                      if (rowInfo) {
                        return {
                          onClick: () => {
                            setSearchTerm('');
                            seStakeholder(rowInfo.original);
                            setStep(prevStep => prevStep + 1);
                          },
                        };
                      }
                      return {};
                    }}
                  />
                );
              }}
            </Query>
          </Box>
        );
      case 1:
        return (
          stakeholder && (
            <Box justifyContent="flex-start" alignItems="flex-start" width="100%">
              <H4 fontWeight="lighter">Step 2: Select Property</H4>
              <Query query={SearchProperties} variables={{ id: stakeholder.id }}>
                {({ loading, error, data }) => {
                  if (loading) {
                    return (
                      <Box width="100%" justifyContent="center" alignItems="center" mb={4}>
                        <Loader color={theme.colors.putty} />
                      </Box>
                    );
                  }
                  if (error) {
                    return <Error />;
                  }
                  if (!loading && data && data.properties.length === 0) {
                    return (
                      <NoDataHandler mt={4} size={50} width="100%" px={2} py={2} text="This user has no properties." />
                    );
                  }
                  return (
                    <React.Fragment>
                      <Box
                        px={2}
                        py={1}
                        my={4}
                        flexDirection="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        bg="paleGrey"
                        borderRadius="4px"
                      >
                        <Span>{stakeholder.firstName + ' ' + stakeholder.lastName}</Span>
                      </Box>
                      <StyledSearchTable
                        data={data.properties}
                        columns={[{ Header: 'Address', accessor: 'address' }]}
                        className="-striped -highlight"
                        defaultPageSize={5}
                        showPageSizeOptions={false}
                        getTrProps={(trprops: ComponentDecoratorProps, rowInfo: RowInfo) => {
                          if (rowInfo) {
                            return {
                              onClick: () => {
                                setProperty(rowInfo.original);
                                setStep(prevStep => prevStep + 1);
                              },
                            };
                          }
                          return {};
                        }}
                      />
                    </React.Fragment>
                  );
                }}
              </Query>
            </Box>
          )
        );
      case 2:
        return (
          stakeholder &&
          property && (
            <Box justifyContent="flex-start" alignItems="flex-start" width="100%">
              <H4 fontWeight="lighter" mb={4}>
                Step 3: Select Application Type
              </H4>
              <React.Fragment>
                <Box flexDirection="row">
                  <Box
                    px={2}
                    py={1}
                    mb={4}
                    mr={2}
                    justifyContent="center"
                    alignItems="center"
                    bg="paleGrey"
                    borderRadius="4px"
                  >
                    <Span>{stakeholder.firstName + ' ' + stakeholder.lastName}</Span>
                  </Box>
                  <Box
                    px={2}
                    py={1}
                    mb={4}
                    justifyContent="center"
                    alignItems="center"
                    bg="paleGrey"
                    borderRadius="4px"
                  >
                    <Span>{property.address}</Span>
                  </Box>
                </Box>
                <RadioButtons items={APPLICATION_TYPES} onChange={e => setApplicationType(e.target.value)} />
              </React.Fragment>
            </Box>
          )
        );
      default:
        return null;
    }
  }

  function handleSuccess(data: any) {
    context.addToast('Application Created Successfully!', 'SUCCESS');
    props.history.push(`/applications/${data.createApplication.id}/details`);
  }

  /**
   * Set remaining fields which aren't on Stakeholder profile
   * to empty strings so Formik initializes these values as well.
   */
  function getInitialFields() {
    const values = { ...stakeholder, ...property, ...{ standNumber: (property || {}).erfNumber } }
    if (applicationType) {
      switch (applicationType) {
        case 'RESIDENTS_SECURITY_ACCESS':
          return residentSecurityAccessFields(values);
        case 'SHUTTLE_SERVICE':
          return shuttleServiceFields({
            ...values,
            ...{
              authorisedPersonsTitleNameSurname: stakeholder ? `${stakeholder.firstName} ${stakeholder.lastName}` : '',
            }
          });
        case 'SECURITY_ACCESS':
          return securityAccessFields(values);
        case 'SPECIAL_ACCESS':
          return specialAccessFields(values);
        case 'CONTRACTOR_SECURITY_ACCESS':
          return contractorSecurityAccessFields(values);
        case 'SUBCONTRACTOR_SECURITY_ACCESS':
          return subcontractorSecurityAccessFields(values);
        case 'CRIMCHECK':
          return crimCheckFields({});
        case 'GYM_MEMBERSHIP':
          return gymMembershipFields(values);
        case 'PET':
          return petApplicationFields(values);
        case 'BANK_DEBIT_ORDER_INSTRUCTION':
          return bankDebitOrderInstructionFields(values);
        case 'TENNIS_MEMBERSHIP':
          return tennisFormFields(values);
        case 'VEHICLE_SECURITY_ACCESS':
          return vehicleSecurityAccessFields({
            ...values,
            ...{
              ownerTitleNameSurname: stakeholder ? `${stakeholder.firstName} ${stakeholder.lastName}` : '',
            }
          });
        default:
          return {};
      }
    }
    return {};
  }

  return (
    <Box width="100%" flexDirection="column">
      {renderSteps(step)}
      {stakeholder && (
        <Mutation
          mutation={CreateApplication}
          refetchQueries={() => {
            return [{ query: GetAllApplications }];
          }}
          onCompleted={data => handleSuccess(data)}
          awaitRefetchQueries={true}
        >
          {(createApplication, { loading, error, data }) => {
            if (loading) {
              return (
                <Box width="100%" justifyContent="center" alignItems="center" py={4}>
                  <Loader color={theme.colors.putty} />
                </Box>
              );
            }
            if (error) {
              context.addToast(formatGqlError(error), 'ERROR');
            }
            return (
              <Box mt={4} flexDirection="row" width="100%">
                {step > 0 && (
                  <Button
                    type="button"
                    onClick={() => {
                      setStep(prevStep => prevStep - 1);
                      if (stakeholder && step === 1) {
                        seStakeholder(null);
                      }
                      setSearchTerm('');
                      setApplicationType(null);
                    }}
                    bg="coal"
                    mr={2}
                    flex={1}
                    text="Go Back"
                  />
                )}
                {step === 2 && (
                  <Button
                    type="button"
                    onClick={() => {
                      if (!applicationType) {
                        context.addToast('Please select an application type!', 'ERROR');
                        return;
                      }
                      createApplication({
                        variables: {
                          type: applicationType,
                          stakeholderId: stakeholder.id,
                          propertyId: property ? property.id : null,
                          fields: getInitialFields(),
                        },
                      });
                    }}
                    flex={1}
                    text="Finish"
                  />
                )}
              </Box>
            );
          }}
        </Mutation>
      )}
    </Box>
  );
};

export default withRouter(ApplicationsWizard);



// WEBPACK FOOTER //
// ./src/components/ApplicationsWizard.tsx